/**
 * List of storage locations for the current warehouse — search, print label, focus on map.
 */
import React, { useCallback, useMemo } from 'react';
import { Box, IconButton, Tooltip, CircularProgress, Chip } from '@mui/material';
import {
  Print as PrintIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Table, { Column } from '../Table/Table';
import {
  getPaginatedStorageLocations,
  filterStorageLocationsForBulkLabels,
  BULK_LABEL_INCLUDE_TYPES,
  type StorageLocationOption,
} from '../../services/locationApi';
import { useServerSidePagination } from '../../hooks/useServerSidePagination';
import { PaginatedResponse } from '../../types/api';

function mapElementIdForHighlight(loc: StorageLocationOption): string {
  const t = (loc.location_type || '').toUpperCase();
  if (['RACK', 'COLD', 'HAZMAT', 'ZONE', 'AISLE', 'FLOOR'].includes(t)) {
    return String(loc.id);
  }
  if (loc.rack) return String(loc.rack);
  return String(loc.id);
}

interface WarehouseLocationsPanelProps {
  warehouseId: number;
  /** Same `include_types` as bulk “Etykiety” / print_labels (default: BULK_LABEL_INCLUDE_TYPES). */
  includeTypes?: readonly string[];
  onShowOnMap: (mapElementId: string, highlightIds: number[]) => void;
  onPrintLabel: (locationId: number) => void;
  printingLabelId: number | null;
}

const WarehouseLocationsPanel: React.FC<WarehouseLocationsPanelProps> = ({
  warehouseId,
  includeTypes = BULK_LABEL_INCLUDE_TYPES,
  onShowOnMap,
  onPrintLabel,
  printingLabelId,
}) => {
  const { t } = useTranslation();
  const singleTypeFilter = includeTypes.length === 1 ? includeTypes[0] : undefined;

  const fetchLocations = useCallback(
    async (
      page: number,
      pageSize: number,
      search: string
    ): Promise<PaginatedResponse<StorageLocationOption>> => {
      const response = await getPaginatedStorageLocations(warehouseId, {
        page: page + 1,
        page_size: pageSize,
        search: search || undefined,
        type: singleTypeFilter,
      });
      return response;
    },
    [warehouseId, singleTypeFilter]
  );

  const pagination = useServerSidePagination(fetchLocations, {
    initialPageSize: 25,
    debounceMs: 300,
  });

  const rows = useMemo(() => {
    const raw = Array.isArray(pagination.data) ? pagination.data : [];
    const list = singleTypeFilter
      ? raw
      : filterStorageLocationsForBulkLabels(raw, includeTypes);
    return [...list].sort((a, b) =>
      (a.full_location_code || a.code).localeCompare(b.full_location_code || b.code)
    );
  }, [pagination.data, includeTypes, singleTypeFilter]);

  const columns = useMemo<Column<StorageLocationOption>[]>(() => [
    {
      id: 'code',
      label: t('warehouseMap.code'),
      flex: 1.2,
      format: (value: string, row) => row.code || row.name,
    },
    {
      id: 'location_type',
      label: t('warehouseMap.type'),
      flex: 0.8,
      format: (value: string) => (
        <Chip size="small" label={value} variant="outlined" />
      ),
      sortable: false,
    },
    {
      id: 'full_location_code',
      label: t('warehouseMap.locationsPanel.fullCode'),
      flex: 1.3,
      format: (value?: string) => value || '-',
    },
    {
      id: 'id',
      label: t('warehouseMap.locationsPanel.actions'),
      align: 'right',
      flex: 0.7,
      sortable: false,
      format: (_value: number, loc) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', gap: 0.5 }}>
          <Tooltip title={t('warehouseMap.locationsPanel.showOnMap')}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onShowOnMap(mapElementIdForHighlight(loc), [
                  loc.id,
                  ...(loc.rack ? [loc.rack] : []),
                ]);
              }}
            >
              <MapIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('warehouseMap.locationsPanel.printOne')}>
            <span>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onPrintLabel(loc.id);
                }}
                disabled={printingLabelId === loc.id}
              >
                {printingLabelId === loc.id ? (
                  <CircularProgress size={18} />
                ) : (
                  <PrintIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ], [onPrintLabel, onShowOnMap, printingLabelId, t]);

  return (
    <Box sx={{ height: '100%', minHeight: 0, p: 2 }}>
      <Box
        sx={{
          height: '100%',
          minHeight: 0,
          '& .MuiDataGrid-container--top': {
            backgroundColor: '#1f1f1f !important',
            zIndex: 100,
          },
          '& .MuiDataGrid-root .MuiDataGrid-columnHeaders': {
            backgroundColor: '#1f1f1f !important',
            borderBottom: '1px solid rgba(255,255,255,0.12)',
            zIndex: 100,
          },
          '& .MuiDataGrid-root .MuiDataGrid-columnHeader': {
            backgroundColor: '#1f1f1f !important',
          },
          '& .MuiDataGrid-root .MuiDataGrid-columnHeaders .MuiDataGrid-filler': {
            backgroundColor: '#1f1f1f !important',
          },
          '& .MuiDataGrid-root .MuiDataGrid-columnHeaders .MuiDataGrid-scrollbarFiller': {
            backgroundColor: '#1f1f1f !important',
          },
        }}
      >
        <Table
          columns={columns}
          data={rows}
          page={pagination.page}
          rowsPerPage={pagination.pageSize}
          totalRows={pagination.totalCount}
          onPageChange={pagination.handlePageChange}
          onRowsPerPageChange={pagination.handlePageSizeChange}
          rowKey="id"
          loading={pagination.isLoading}
          searchable
          searchPlaceholder={t('warehouseMap.locationsPanel.search')}
          onSearchChange={pagination.handleSearchChange}
          emptyMessage={
            pagination.totalCount === 0
              ? t('warehouseMap.locationsPanel.empty')
              : t('warehouseMap.locationsPanel.noResults')
          }
          title={t('warehouseMap.locationsPanel.title')}
          description={t('warehouseMap.locationsPanel.description')}
        />
      </Box>
    </Box>
  );
};

export default WarehouseLocationsPanel;
